/* ============================================================
   SINGSPACE ADMIN - TABLEAU CLIENTS (Pro Max)
   ============================================================ */

import { AlertCircle, Calendar, ChevronLeft, ChevronRight, Eye, Mail, Phone, SearchX } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';

interface ClientsTableProps {
  clients: any[];
  loading: boolean;
  error: string | null;
  statusFilter: 'all' | 'active' | 'inactive';
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onViewDetails: (clientId: string) => void;
}

export function ClientsTable({
  clients,
  loading,
  error,
  statusFilter,
  currentPage,
  totalPages,
  onPageChange,
  onViewDetails,
}: ClientsTableProps) {
  const filteredClients = clients.filter((client) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return client.isActive;
    if (statusFilter === 'inactive') return !client.isActive;
    return true;
  });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (filteredClients.length === 0) return <EmptyState title="Aucun client trouvé" />;

  return (
    <div className="flex flex-col h-full">
      <div className="overflow-x-auto flex-1">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-50/50 border-b border-gray-100">
              <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                Profil Client
              </th>
              <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                Contact
              </th>
              <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                Activité
              </th>
              <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                Statut
              </th>
              <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredClients.map((client) => (
              <tr key={client.id} className="hover:bg-slate-50/30 transition-colors group text-sm">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#00758d]/5 text-[#00758d] flex items-center justify-center font-black text-xs border border-[#00758d]/10 uppercase">
                      {(client.firstName[0] + client.lastName[0]).toUpperCase()}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-[#00303c] leading-tight">
                        {client.firstName} {client.lastName}
                      </span>
                      <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-0.5">
                        Membre depuis {new Date(client.createdAt).getFullYear()}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-slate-600 font-medium text-xs">
                      <Mail size={12} className="text-[#00758d] opacity-50" />
                      {client.email}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600 font-medium text-xs">
                      <Phone size={12} className="text-[#00758d] opacity-50" />
                      {client.phone}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#dfc32f]/5 border border-[#dfc32f]/20 text-[#8a7600]">
                    <Calendar size={12} className="opacity-60" />
                    <span className="text-[10px] font-black uppercase tracking-wider">
                      {client.bookingsCount || 0} réservations
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${client.isActive ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}
                  >
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${client.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`}
                    />
                    {client.isActive ? 'Actif' : 'Bloqué'}
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(client.id)}
                      className="h-9 w-9 rounded-xl text-[#00758d] hover:bg-[#00758d]/10"
                    >
                      <Eye size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="px-6 py-4 bg-slate-50/50 border-t border-gray-100 flex items-center justify-between">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            Page {currentPage} sur {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
              className="h-8 w-8 p-0 rounded-lg"
            >
              <ChevronLeft size={16} />
            </Button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`w-8 h-8 rounded-lg text-[10px] font-black transition-all ${page === currentPage ? 'bg-[#00303c] text-white' : 'hover:bg-gray-200 text-slate-400'}`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              className="h-8 w-8 p-0 rounded-lg"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <LoadingSpinner message="Synchronisation de la base client..." submessage="" size="md" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-rose-500">
      <AlertCircle size={40} className="mb-4 opacity-50" />
      <h3 className="font-black uppercase tracking-widest text-xs mb-1">Erreur réseau</h3>
      <p className="text-sm font-medium opacity-70">{message}</p>
    </div>
  );
}

function EmptyState({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-slate-300">
      <SearchX size={48} className="mb-4 opacity-20" />
      <h3 className="font-black uppercase tracking-widest text-xs mb-1">{title}</h3>
      <p className="text-xs font-medium opacity-50 text-center max-w-xs">Aucun client ne correspond à vos critères.</p>
    </div>
  );
}
